import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";

const MOURenewalForm = () => {
  const [formData, setFormData] = useState({
    mouid: "",
    dateOfRenew: "",
    dateOfEnd: "",
    validity: "", // Auto-calculated validity
    document: "",
    filename: "",
  });

  const [mouDetails, setMouDetails] = useState(null); // ✅ State for MOU Details

  // ✅ Load mouid from localStorage when form loads
  useEffect(() => {
    const storedMouId = localStorage.getItem("mouid");
    if (storedMouId) {
      setFormData((prevData) => ({
        ...prevData,
        mouid: storedMouId, // Display full string in the form
      }));

      // ✅ Fetch MOU details using GET request if mouid is available
      fetchMouDetails(storedMouId.split(" - ")[0].trim()); // Get only MOU ID
    }
  }, []);

 // ✅ Fetch MOU details from backend using GET request
const fetchMouDetails = async (mouid) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/mou-renewals/mou/${mouid}`
      );
      const data = response.data;
  
      if (Array.isArray(data)) {
        // ✅ Set multiple MOU details in the state for table display
        setMouDetails(data);
      } else {
        setMouDetails([]);
      }
    } catch (error) {
      console.error("Error fetching MOU details:", error);
      Swal.fire({
        icon: "error",
        title: "Error Fetching Data",
        text: "Failed to retrieve MOU details.",
      });
    }
  };
  

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Calculate validity if dateOfRenew or dateOfEnd changes
    if (name === "dateOfRenew" || name === "dateOfEnd") {
      calculateValidity(
        name === "dateOfRenew" ? value : formData.dateOfRenew,
        name === "dateOfEnd" ? value : formData.dateOfEnd
      );
    }
  };

  // Function to calculate validity in years, months, and days
  const calculateValidity = (start, end) => {
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);

      if (endDate >= startDate) {
        let yearDiff = endDate.getFullYear() - startDate.getFullYear();
        let monthDiff = endDate.getMonth() - startDate.getMonth();
        let dayDiff = endDate.getDate() - startDate.getDate();

        if (dayDiff < 0) {
          monthDiff -= 1;
          const prevMonth = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            0
          ).getDate();
          dayDiff += prevMonth;
        }

        if (monthDiff < 0) {
          yearDiff -= 1;
          monthDiff += 12;
        }

        const validityString = `${
          yearDiff > 0 ? `${yearDiff} year${yearDiff > 1 ? "s" : ""}` : ""
        } ${monthDiff > 0 ? `${monthDiff} month${monthDiff > 1 ? "s" : ""}` : ""} ${
          dayDiff > 0 ? `${dayDiff} day${dayDiff > 1 ? "s" : ""}` : ""
        }`.trim();

        setFormData((prevState) => ({
          ...prevState,
          validity: validityString || "0 days",
        }));
      } else {
        setFormData((prevState) => ({
          ...prevState,
          validity: "Invalid date range",
        }));
      }
    }
  };

  // Handle file upload and convert to Base64
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fileNameWithoutExt = file.name.replace(/\.(pdf|doc|docx)$/, "");
        setFormData({
          ...formData,
          document: reader.result.split(",")[1],
          filename: fileNameWithoutExt,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const extractedMouId = formData.mouid.split(" - ")[0].trim();
  
    const payload = {
      ...formData,
      mouid: extractedMouId,
      dateOfRenew: new Date(formData.dateOfRenew).toISOString(),
      dateOfEnd: new Date(formData.dateOfEnd).toISOString().split("T")[0],
    };
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/mou-renewals/create`,
        payload
      );
  
      // ✅ Show success message and go back after clicking 'OK'
      Swal.fire({
        icon: "success",
        title: "MOU Renewal Created!",
        text: "MOU Renewal Created Successfully!",
      }).then(() => {
        window.history.back(); 
      });
  
      console.log(response.data);
  
      await fetchMouDetails(extractedMouId);
    } catch (error) {
      console.error("Error creating MOU:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Create MOU",
        text: "Please Try Again!",
      });
    }
  };
  

  return (
    <div className="container mt-5">
      <div className="row">
        {/* Left Side: MOU Form */}
        <div className="col-md-6">
          <h2 className="mb-4">MOU Renewal Form</h2>
          <form onSubmit={handleSubmit} className="shadow p-4 bg-light rounded">
            {/* MOU ID (Read-Only) */}
            <div className="mb-3">
              <label className="form-label">MOU ID-Title</label>
              <input
                type="text"
                className="form-control"
                name="mouid"
                value={formData.mouid}
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Date of Renewal</label>
              <input
                type="date"
                className="form-control"
                name="dateOfRenew"
                value={formData.dateOfRenew}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Date of End</label>
              <input
                type="date"
                className="form-control"
                name="dateOfEnd"
                value={formData.dateOfEnd}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Validity</label>
              <input
                type="text"
                className="form-control"
                name="validity"
                value={formData.validity}
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Upload Document (PDF or Doc)</label>
              <input
                type="file"
                className="form-control"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">File Name</label>
              <input
                type="text"
                className="form-control"
                name="filename"
                value={formData.filename}
                onChange={handleChange}
              />
            </div>

            <button type="submit" className="btn btn-primary">
              Submit MOU Renewal
            </button>
          </form>
        </div>
{/* Right Side: MOU Details Table */}
<div className="col-md-6">
  <h2 className="mb-4">MOU Details</h2>
  <div
    className="table-responsive shadow bg-white rounded"
    style={{ maxHeight: "400px", overflowY: "auto" }}
  >
    {mouDetails && mouDetails.length > 0 ? (
      <table className="table table-bordered table-hover">
        <thead className="table-dark">
          <tr>
            <th>MOU ID</th>
            <th>Date of Renewal</th>
            <th>Date of End</th>
            <th>File Name</th>
            <th>View</th>
          </tr>
        </thead>
        <tbody>
          {mouDetails.map((mou, index) => (
            <tr key={index}>
              <td>{mou[1]}</td>
              <td>{mou[0].split("T")[0]}</td>
              <td>{mou[2]}</td>
              <td>{mou[3]}</td>
              <td>
  <a href={`${process.env.REACT_APP_FILE_URL || 'https://ooo-toddler-gifts-pubs.trycloudflare.comloudflare.com'}/uploads/${mou[3]}`} target="_blank" rel="noopener noreferrer">
    View Document
  </a>
</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="text-muted">No MOU details found.</p>
    )}
  </div>
</div>

      </div>
    </div>
  );
};

export default MOURenewalForm;
